import React, { useState } from 'react';
import { Section } from '../../stores/builderStore';
import { Link, Plus, Trash2, GripVertical, Image as ImageIcon } from 'lucide-react';
import RichTextEditor from '../RichTextEditor';

interface ContentEditorProps {
  section: Section;
  onChange: (props: Record<string, any>) => void;
}

export const ContentEditor: React.FC<ContentEditorProps> = ({ section, onChange }) => {

  // Use type or fallback to section_type
  const sectionType = section.type || (section as any).section_type || '';

  // Debug logging
  console.log('ContentEditor received section:', {
    id: section.id,
    type: section.type,
    section_type: (section as any).section_type,
    effectiveType: sectionType,
    title: section.title,
    hasType: !!section.type,
    allKeys: Object.keys(section)
  });

  const handlePropChange = (key: string, value: any) => {
    onChange({ [key]: value });
  };

  const handleArrayItemChange = (arrayKey: string, index: number, field: string, value: any) => {
    const items = [...(section.props[arrayKey] || [])];

    // Ensure the item exists at this index
    if (!items[index]) {
      console.error(`Item at index ${index} doesn't exist in ${arrayKey}!`);
      items[index] = {};
    }

    items[index] = { ...items[index], [field]: value };
    console.log(`Updating ${arrayKey}[${index}].${field} to:`, value);
    console.log('Current section props before update:', section.props);
    console.log('Updated items array:', items);

    // Call onChange with the updated items
    const updatedProps = { [arrayKey]: items };
    console.log('Calling onChange with:', updatedProps);
    onChange(updatedProps);
  };

  const addArrayItem = (arrayKey: string, template: any) => {
    const items = [...(section.props[arrayKey] || [])];
    items.push(template);
    onChange({ [arrayKey]: items });
  };

  const removeArrayItem = (arrayKey: string, index: number) => {
    const items = [...(section.props[arrayKey] || [])];
    items.splice(index, 1);
    onChange({ [arrayKey]: items });
  };


  // Render content based on section type
  const renderContentFields = () => {
    switch (sectionType) {
      case 'hero':
      case 'hero_center':
      case 'hero_split':
      case 'hero_banner':
      case 'hero_video':
        return (
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label htmlFor="hero-title" className="block text-sm font-medium mb-1">Title</label>
              <input
                id="hero-title"
                name="title"
                type="text"
                value={section.props.title || ''}
                onChange={(e) => handlePropChange('title', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter title"
              />
            </div>

            {/* Subtitle */}
            <div>
              <label htmlFor="hero-subtitle" className="block text-sm font-medium mb-1">Subtitle</label>
              <textarea
                id="hero-subtitle"
                name="subtitle"
                value={section.props.subtitle || ''}
                onChange={(e) => handlePropChange('subtitle', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="Enter subtitle"
              />
            </div>

            {/* Text Alignment */}
            <div>
              <label className="block text-sm font-medium mb-1">Text Alignment</label>
              <select
                value={section.props.textAlign || 'center'}
                onChange={(e) => handlePropChange('textAlign', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>

            {/* Text Position (vertical) */}
            <div>
              <label className="block text-sm font-medium mb-1">Text Vertical Position</label>
              <select
                value={section.props.textPosition || 'center'}
                onChange={(e) => handlePropChange('textPosition', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="top">Top</option>
                <option value="center">Center</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>

            {/* Overlay Intensity */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Overlay Intensity: {Math.round((section.props.overlayIntensity || 0.4) * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={(section.props.overlayIntensity || 0.4) * 100}
                onChange={(e) => handlePropChange('overlayIntensity', parseInt(e.target.value) / 100)}
                className="w-full"
              />
            </div>

            {/* Primary Button Settings */}
            <div className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Primary Button</p>
                <input
                  type="checkbox"
                  checked={section.props.showPrimaryButton !== false}
                  onChange={(e) => handlePropChange('showPrimaryButton', e.target.checked)}
                  className="rounded"
                />
              </div>
              {section.props.showPrimaryButton !== false && (
                <>
                  <input
                    type="text"
                    value={section.props.buttonText || ''}
                    onChange={(e) => handlePropChange('buttonText', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                    placeholder="Button Text"
                  />
                  <div className="flex gap-2">
                    <select
                      value={section.props.buttonLinkType || 'internal'}
                      onChange={(e) => handlePropChange('buttonLinkType', e.target.value)}
                      className="px-2 py-1 border rounded text-sm"
                    >
                      <option value="internal">Internal</option>
                      <option value="external">External</option>
                    </select>
                    <input
                      type="text"
                      value={section.props.buttonLink || ''}
                      onChange={(e) => handlePropChange('buttonLink', e.target.value)}
                      className="flex-1 px-2 py-1 border rounded text-sm"
                      placeholder={section.props.buttonLinkType === 'external' ? 'https://...' : '/page-route'}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Secondary Button Settings */}
            <div className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Secondary Button</p>
                <input
                  type="checkbox"
                  checked={section.props.showSecondaryButton === true}
                  onChange={(e) => handlePropChange('showSecondaryButton', e.target.checked)}
                  className="rounded"
                />
              </div>
              {section.props.showSecondaryButton && (
                <>
                  <input
                    type="text"
                    value={section.props.secondaryButtonText || ''}
                    onChange={(e) => handlePropChange('secondaryButtonText', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                    placeholder="Button Text"
                  />
                  <div className="flex gap-2">
                    <select
                      value={section.props.secondaryButtonLinkType || 'internal'}
                      onChange={(e) => handlePropChange('secondaryButtonLinkType', e.target.value)}
                      className="px-2 py-1 border rounded text-sm"
                    >
                      <option value="internal">Internal</option>
                      <option value="external">External</option>
                    </select>
                    <input
                      type="text"
                      value={section.props.secondaryButtonLink || ''}
                      onChange={(e) => handlePropChange('secondaryButtonLink', e.target.value)}
                      className="flex-1 px-2 py-1 border rounded text-sm"
                      placeholder={section.props.secondaryButtonLinkType === 'external' ? 'https://...' : '/page-route'}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Button Alignment */}
            {(section.props.showPrimaryButton !== false || section.props.showSecondaryButton) && (
              <div>
                <label className="block text-sm font-medium mb-1">Button Alignment</label>
                <select
                  value={section.props.buttonAlign || 'center'}
                  onChange={(e) => handlePropChange('buttonAlign', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            )}

            {/* Background Image URL */}
            <div>
              <label className="block text-sm font-medium mb-1">Background Image URL</label>
              <div className="space-y-2">
                <input
                  type="text"
                  value={section.props.backgroundImage || ''}
                  onChange={(e) => handlePropChange('backgroundImage', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                />
                {section.props.backgroundImage && (
                  <div className="relative">
                    <img
                      src={section.props.backgroundImage}
                      alt="Background Preview"
                      className="w-full h-32 object-cover rounded-lg"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500">Use Media Manager to upload images and get URLs</p>
              </div>
            </div>

            {/* Height */}
            <div>
              <label className="block text-sm font-medium mb-1">Height</label>
              <select
                value={section.props.height || '600px'}
                onChange={(e) => handlePropChange('height', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="400px">Small (400px)</option>
                <option value="500px">Medium (500px)</option>
                <option value="600px">Large (600px)</option>
                <option value="700px">Extra Large (700px)</option>
                <option value="100vh">Full Height</option>
              </select>
            </div>

            {/* Alignment */}
            <div>
              <label className="block text-sm font-medium mb-1">Content Alignment</label>
              <div className="grid grid-cols-3 gap-2">
                {['left', 'center', 'right'].map(align => (
                  <button
                    key={align}
                    onClick={() => handlePropChange('alignment', align)}
                    className={`px-3 py-2 text-sm rounded border capitalize ${
                      section.props.alignment === align
                        ? 'bg-blue-100 border-blue-500'
                        : 'bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {align}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'features':
      case 'feature_cards':
      case 'features_3col':
      case 'features_zigzag':
      case 'features_cards':
        return (
          <div className="space-y-4">
            {/* Section Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Section Title</label>
              <input
                type="text"
                value={section.props.title || ''}
                onChange={(e) => handlePropChange('title', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Our Features"
              />
            </div>

            {/* Section Subtitle */}
            <div>
              <label className="block text-sm font-medium mb-1">Section Subtitle</label>
              <input
                type="text"
                value={section.props.subtitle || ''}
                onChange={(e) => handlePropChange('subtitle', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Optional subtitle text"
              />
            </div>

            {/* Section Title Typography */}
            <div className="space-y-2 border rounded-lg p-3 bg-gray-50">
              <p className="text-sm font-medium">Section Title Typography</p>

              <div className="grid grid-cols-2 gap-2">
                {/* Title Alignment */}
                <div>
                  <label className="text-xs text-gray-600">Title Align</label>
                  <select
                    value={section.props.titleAlign || 'center'}
                    onChange={(e) => handlePropChange('titleAlign', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>

                {/* Title Size */}
                <div>
                  <label className="text-xs text-gray-600">Title Size</label>
                  <select
                    value={section.props.titleSize || 'text-3xl'}
                    onChange={(e) => handlePropChange('titleSize', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    <option value="text-xl">XL (20px)</option>
                    <option value="text-2xl">2XL (24px)</option>
                    <option value="text-3xl">3XL (30px)</option>
                    <option value="text-4xl">4XL (36px)</option>
                    <option value="text-5xl">5XL (48px)</option>
                    <option value="text-6xl">6XL (60px)</option>
                  </select>
                </div>

                {/* Title Weight */}
                <div>
                  <label className="text-xs text-gray-600">Title Weight</label>
                  <select
                    value={section.props.titleWeight || 'font-bold'}
                    onChange={(e) => handlePropChange('titleWeight', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    <option value="font-normal">Normal</option>
                    <option value="font-medium">Medium</option>
                    <option value="font-semibold">Semibold</option>
                    <option value="font-bold">Bold</option>
                    <option value="font-extrabold">Extra Bold</option>
                  </select>
                </div>

                {/* Subtitle Size */}
                <div>
                  <label className="text-xs text-gray-600">Subtitle Size</label>
                  <select
                    value={section.props.subtitleSize || 'text-lg'}
                    onChange={(e) => handlePropChange('subtitleSize', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    <option value="text-sm">Small</option>
                    <option value="text-base">Base</option>
                    <option value="text-lg">Large</option>
                    <option value="text-xl">XL</option>
                  </select>
                </div>

                {/* Title Gap */}
                <div>
                  <label className="text-xs text-gray-600">Title Gap</label>
                  <select
                    value={section.props.titleGap || 'mb-8'}
                    onChange={(e) => handlePropChange('titleGap', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    <option value="mb-2">Tiny (8px)</option>
                    <option value="mb-4">Small (16px)</option>
                    <option value="mb-6">Medium (24px)</option>
                    <option value="mb-8">Large (32px)</option>
                    <option value="mb-10">XL (40px)</option>
                    <option value="mb-12">2XL (48px)</option>
                    <option value="mb-16">3XL (64px)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Premium Style Variant */}
            <div>
              <label className="block text-sm font-medium mb-1">Style Variant</label>
              <select
                value={section.props.variant || 'overlay'}
                onChange={(e) => handlePropChange('variant', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="default">Default Grid (Vertical)</option>
                <option value="overlay">🎨 Overlay Cards (Horizontal)</option>
                <option value="netflix">🎬 Netflix Style (Horizontal)</option>
                <option value="appstore">📱 App Store Style (Horizontal)</option>
                <option value="glass">✨ Glass Style (Horizontal)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                All premium styles enable horizontal scrolling with navigation arrows
              </p>
            </div>

            {/* Columns (only for default variant) */}
            {section.props.variant === 'default' && (
              <div>
                <label className="block text-sm font-medium mb-1">Columns</label>
                <select
                  value={section.props.columns || 3}
                  onChange={(e) => handlePropChange('columns', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value={2}>2 Columns</option>
                  <option value={3}>3 Columns</option>
                  <option value={4}>4 Columns</option>
                </select>
              </div>
            )}

            {/* Feature Items */}
            <div>
              <label className="block text-sm font-medium mb-2">Feature Items</label>
              <div className="space-y-2">
                {(section.props.items || []).map((item: any, index: number) => (
                  <div key={index} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Feature {index + 1}</span>
                      <button
                        onClick={() => removeArrayItem('items', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Title */}
                    <div>
                      <label className="text-xs font-medium text-gray-700">Title</label>
                      <input
                        type="text"
                        value={item.title || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'title', e.target.value)}
                        placeholder="Feature Title"
                        className="w-full px-2 py-1 border rounded text-sm"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="text-xs font-medium text-gray-700">Description</label>
                      <textarea
                        value={item.description || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'description', e.target.value)}
                        placeholder="Feature description"
                        className="w-full px-2 py-1 border rounded text-sm"
                        rows={2}
                      />
                    </div>

                    {/* Typography Settings */}
                    <div className="space-y-2 border rounded p-2 bg-gray-50">
                      <p className="text-xs font-medium text-gray-700">Typography & Layout</p>

                      <div className="grid grid-cols-2 gap-2">
                        {/* Text Alignment */}
                        <div>
                          <label className="text-xs text-gray-600">Text Align</label>
                          <select
                            value={item.textAlign || 'left'}
                            onChange={(e) => handleArrayItemChange('items', index, 'textAlign', e.target.value)}
                            className="w-full px-1 py-1 border rounded text-xs"
                          >
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>
                        </div>

                        {/* Title Font Size */}
                        <div>
                          <label className="text-xs text-gray-600">Title Size</label>
                          <select
                            value={item.titleSize || 'text-lg'}
                            onChange={(e) => handleArrayItemChange('items', index, 'titleSize', e.target.value)}
                            className="w-full px-1 py-1 border rounded text-xs"
                          >
                            <option value="text-sm">Small</option>
                            <option value="text-base">Medium</option>
                            <option value="text-lg">Large</option>
                            <option value="text-xl">X-Large</option>
                            <option value="text-2xl">2X-Large</option>
                          </select>
                        </div>

                        {/* Title Font Weight */}
                        <div>
                          <label className="text-xs text-gray-600">Title Weight</label>
                          <select
                            value={item.titleWeight || 'font-semibold'}
                            onChange={(e) => handleArrayItemChange('items', index, 'titleWeight', e.target.value)}
                            className="w-full px-1 py-1 border rounded text-xs"
                          >
                            <option value="font-normal">Normal</option>
                            <option value="font-medium">Medium</option>
                            <option value="font-semibold">Semibold</option>
                            <option value="font-bold">Bold</option>
                          </select>
                        </div>

                        {/* Description Font Size */}
                        <div>
                          <label className="text-xs text-gray-600">Desc Size</label>
                          <select
                            value={item.descSize || 'text-sm'}
                            onChange={(e) => handleArrayItemChange('items', index, 'descSize', e.target.value)}
                            className="w-full px-1 py-1 border rounded text-xs"
                          >
                            <option value="text-xs">X-Small</option>
                            <option value="text-sm">Small</option>
                            <option value="text-base">Medium</option>
                            <option value="text-lg">Large</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Image */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-700">Image</label>
                      {item.image && !item.image.startsWith('blob:') && (
                        <div className="relative">
                          <img
                            src={item.image}
                            alt="Feature image"
                            className="w-full h-24 object-cover rounded"
                          />
                          <button
                            onClick={() => handleArrayItemChange('items', index, 'image', '')}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <input
                        type="text"
                        value={item.image || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'image', e.target.value)}
                        placeholder="Image URL (use Media Manager for uploads)"
                        className="w-full px-2 py-1 border rounded text-sm"
                      />
                    </div>

                    {/* Call to Action */}
                    <div className="space-y-2 border-t pt-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-700">Call to Action</label>
                        <div className="flex items-center gap-1">
                          <label className="text-xs text-gray-600">Show CTA</label>
                          <input
                            type="checkbox"
                            checked={item.showCTA !== false}
                            onChange={(e) => handleArrayItemChange('items', index, 'showCTA', e.target.checked)}
                            className="rounded"
                          />
                        </div>
                      </div>

                      {item.showCTA !== false && (
                        <>
                          {/* Button Text */}
                          <input
                            type="text"
                            value={item.ctaText || item.buttonText || ''}
                            onChange={(e) => handleArrayItemChange('items', index, 'ctaText', e.target.value)}
                            placeholder="Button Text (e.g., Learn More)"
                            className="w-full px-2 py-1 border rounded text-sm"
                          />

                          {/* Link Type and URL */}
                          <div className="flex gap-2">
                            <select
                              value={item.linkType || 'internal'}
                              onChange={(e) => handleArrayItemChange('items', index, 'linkType', e.target.value)}
                              className="px-2 py-1 border rounded text-sm"
                            >
                              <option value="internal">Internal</option>
                              <option value="external">External</option>
                            </select>
                            <input
                              type="text"
                              value={item.link || ''}
                              onChange={(e) => handleArrayItemChange('items', index, 'link', e.target.value)}
                              placeholder={item.linkType === 'external' ? 'https://...' : '/page-route'}
                              className="flex-1 px-2 py-1 border rounded text-sm"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => addArrayItem('items', {
                    image: '',
                    title: 'New Feature',
                    description: 'Feature description',
                    ctaText: 'Learn More',
                    link: '',
                    linkType: 'internal'
                  })}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Feature
                </button>
              </div>
            </div>
          </div>
        );

      case 'cta':
      case 'cta_simple':
      case 'cta_newsletter':
      case 'call_to_action':
        return (
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                type="text"
                value={section.props.title || ''}
                onChange={(e) => handlePropChange('title', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ready to get started?"
              />
            </div>

            {/* Subtitle */}
            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <textarea
                value={section.props.subtitle || ''}
                onChange={(e) => handlePropChange('subtitle', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="Join thousands of satisfied customers"
              />
            </div>

            {/* Primary Button */}
            <div className="border rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium">Primary Button</p>
              <input
                type="text"
                value={section.props.buttonText || ''}
                onChange={(e) => handlePropChange('buttonText', e.target.value)}
                placeholder="Button text"
                className="w-full px-2 py-1 border rounded text-sm"
              />
              <input
                type="text"
                value={section.props.buttonLink || ''}
                onChange={(e) => handlePropChange('buttonLink', e.target.value)}
                placeholder="Button link"
                className="w-full px-2 py-1 border rounded text-sm"
              />
            </div>

            {/* Secondary Button */}
            <div className="border rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium">Secondary Button (Optional)</p>
              <input
                type="text"
                value={section.props.secondaryButtonText || ''}
                onChange={(e) => handlePropChange('secondaryButtonText', e.target.value)}
                placeholder="Button text"
                className="w-full px-2 py-1 border rounded text-sm"
              />
              <input
                type="text"
                value={section.props.secondaryButtonLink || ''}
                onChange={(e) => handlePropChange('secondaryButtonLink', e.target.value)}
                placeholder="Button link"
                className="w-full px-2 py-1 border rounded text-sm"
              />
            </div>
          </div>
        );

      case 'testimonials':
      case 'testimonials_carousel':
      case 'testimonials_grid':
      case 'customer_testimonials':
        return (
          <div className="space-y-4">
            {/* Section Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Section Title</label>
              <input
                type="text"
                value={section.props.title || ''}
                onChange={(e) => handlePropChange('title', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="What Our Customers Say"
              />
            </div>

            {/* Auto-play */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoplay"
                checked={section.props.autoplay || false}
                onChange={(e) => handlePropChange('autoplay', e.target.checked)}
                className="rounded"
              />
              <label htmlFor="autoplay" className="text-sm">Auto-play carousel</label>
            </div>

            {/* Interval */}
            {section.props.autoplay && (
              <div>
                <label className="block text-sm font-medium mb-1">Interval (seconds)</label>
                <input
                  type="number"
                  value={(section.props.interval || 5000) / 1000}
                  onChange={(e) => handlePropChange('interval', parseInt(e.target.value) * 1000)}
                  className="w-full px-3 py-2 border rounded-lg"
                  min={1}
                  max={10}
                />
              </div>
            )}

            {/* Testimonials */}
            <div>
              <label className="block text-sm font-medium mb-2">Testimonials</label>
              <div className="space-y-2">
                {(section.props.items || []).map((item: any, index: number) => (
                  <div key={index} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Testimonial {index + 1}</span>
                      <button
                        onClick={() => removeArrayItem('items', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <textarea
                      value={item.quote || ''}
                      onChange={(e) => handleArrayItemChange('items', index, 'quote', e.target.value)}
                      placeholder="Customer testimonial..."
                      className="w-full px-2 py-1 border rounded text-sm"
                      rows={3}
                    />

                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={item.author || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'author', e.target.value)}
                        placeholder="Author name"
                        className="px-2 py-1 border rounded text-sm"
                      />
                      <input
                        type="text"
                        value={item.role || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'role', e.target.value)}
                        placeholder="Role/Company"
                        className="px-2 py-1 border rounded text-sm"
                      />
                    </div>

                    <input
                      type="text"
                      value={item.avatar || ''}
                      onChange={(e) => handleArrayItemChange('items', index, 'avatar', e.target.value)}
                      placeholder="Avatar URL (optional)"
                      className="w-full px-2 py-1 border rounded text-sm"
                    />
                  </div>
                ))}

                <button
                  onClick={() => addArrayItem('items', {
                    quote: 'This product changed our business!',
                    author: 'John Doe',
                    role: 'CEO at Company',
                    avatar: ''
                  })}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Testimonial
                </button>
              </div>
            </div>
          </div>
        );

      case 'gallery':
      case 'gallery_grid':
      case 'gallery_masonry':
      case 'content_grid':
        return (
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Gallery Title</label>
              <input
                type="text"
                value={section.props.title || ''}
                onChange={(e) => handlePropChange('title', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Our Work"
              />
            </div>

            {/* Columns */}
            <div>
              <label className="block text-sm font-medium mb-1">Columns</label>
              <select
                value={section.props.columns || 4}
                onChange={(e) => handlePropChange('columns', parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value={2}>2 Columns</option>
                <option value={3}>3 Columns</option>
                <option value={4}>4 Columns</option>
                <option value={5}>5 Columns</option>
                <option value={6}>6 Columns</option>
              </select>
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium mb-2">Gallery Images</label>
              <div className="space-y-2">
                {(section.props.images || []).map((image: any, index: number) => (
                  <div key={index} className="flex items-center gap-2">
                    <img
                      src={image.url || image}
                      alt={`Gallery ${index + 1}`}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <input
                      type="text"
                      value={image.caption || ''}
                      onChange={(e) => handleArrayItemChange('images', index, 'caption', e.target.value)}
                      placeholder="Caption (optional)"
                      className="flex-1 px-2 py-1 border rounded text-sm"
                    />
                    <button
                      onClick={() => removeArrayItem('images', index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                <button
                  onClick={() => {
                    const url = prompt('Enter image URL:');
                    if (url) {
                      const images = section.props.images || [];
                      handlePropChange('images', [...images, { url }]);
                    }
                  }}
                  className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-blue-500 text-sm text-gray-600"
                >
                  <ImageIcon className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                  Add Image URL
                </button>
                <p className="text-xs text-gray-500">Use Media Manager to upload images and get URLs</p>
              </div>
            </div>
          </div>
        );

      case 'quick_actions':
        return (
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                type="text"
                value={section.props.title || ''}
                onChange={(e) => handlePropChange('title', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Quick Start"
              />
            </div>

            {/* Subtitle */}
            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <input
                type="text"
                value={section.props.subtitle || ''}
                onChange={(e) => handlePropChange('subtitle', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Jump right into learning"
              />
            </div>

            {/* Actions */}
            <div>
              <label className="block text-sm font-medium mb-2">Action Buttons</label>
              <div className="space-y-2">
                {(section.props.actions || []).map((action: any, index: number) => (
                  <div key={index} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Action {index + 1}</span>
                      <button
                        onClick={() => removeArrayItem('actions', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-600">Icon (emoji or text)</label>
                        <input
                          type="text"
                          value={action.icon || ''}
                          onChange={(e) => handleArrayItemChange('actions', index, 'icon', e.target.value)}
                          placeholder="📚"
                          className="w-full px-2 py-1 border rounded text-sm text-center text-2xl"
                          maxLength={2}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">Label</label>
                        <input
                          type="text"
                          value={action.label || ''}
                          onChange={(e) => handleArrayItemChange('actions', index, 'label', e.target.value)}
                          placeholder="Study Materials"
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-gray-600">Link (optional)</label>
                      <input
                        type="text"
                        value={action.link || ''}
                        onChange={(e) => handleArrayItemChange('actions', index, 'link', e.target.value)}
                        placeholder="/materials or https://..."
                        className="w-full px-2 py-1 border rounded text-sm"
                      />
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => addArrayItem('actions', {
                    icon: '⚡',
                    label: 'New Action',
                    link: '#'
                  })}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Action Button
                </button>
              </div>
            </div>

            {/* Columns */}
            <div>
              <label className="block text-sm font-medium mb-1">Grid Columns</label>
              <select
                value={section.props.columns || 4}
                onChange={(e) => handlePropChange('columns', parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value={2}>2 Columns</option>
                <option value={3}>3 Columns</option>
                <option value={4}>4 Columns</option>
                <option value={6}>6 Columns</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">On mobile, this will automatically adjust to 2 columns</p>
            </div>
          </div>
        );

      case 'stats':
      case 'stats_counter':
      case 'quick_stats':
        return (
          <div className="space-y-4">
            {/* Stats Items */}
            <div>
              <label className="block text-sm font-medium mb-2">Statistics</label>
              <div className="space-y-2">
                {(section.props.items || []).map((item: any, index: number) => (
                  <div key={index} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Stat {index + 1}</span>
                      <button
                        onClick={() => removeArrayItem('items', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={item.number || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'number', e.target.value)}
                        placeholder="Number (e.g., 1000+)"
                        className="px-2 py-1 border rounded text-sm"
                      />
                      <input
                        type="text"
                        value={item.label || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'label', e.target.value)}
                        placeholder="Label"
                        className="px-2 py-1 border rounded text-sm"
                      />
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => addArrayItem('items', {
                    number: '100+',
                    label: 'Happy Customers'
                  })}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Statistic
                </button>
              </div>
            </div>
          </div>
        );

      case 'carousel':
      case 'new_carousel':
        return (
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Section Title</label>
              <input
                type="text"
                value={section.props.title || ''}
                onChange={(e) => handlePropChange('title', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter carousel title"
              />
            </div>

            {/* Subtitle */}
            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <textarea
                value={section.props.subtitle || ''}
                onChange={(e) => handlePropChange('subtitle', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
                placeholder="Enter subtitle"
              />
            </div>

            {/* Button */}
            <div className="border rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium">Button</p>
              <input
                type="text"
                value={section.props.buttonText || ''}
                onChange={(e) => handlePropChange('buttonText', e.target.value)}
                placeholder="Button text"
                className="w-full px-2 py-1 border rounded text-sm"
              />
              <input
                type="text"
                value={section.props.buttonLink || ''}
                onChange={(e) => handlePropChange('buttonLink', e.target.value)}
                placeholder="Button link"
                className="w-full px-2 py-1 border rounded text-sm"
              />
            </div>

            {/* Background Image URL */}
            <div>
              <label className="block text-sm font-medium mb-1">Background Image URL</label>
              <div className="space-y-2">
                <input
                  type="text"
                  value={section.props.backgroundImage || ''}
                  onChange={(e) => handlePropChange('backgroundImage', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                />
                {section.props.backgroundImage && (
                  <div className="relative">
                    <img
                      src={section.props.backgroundImage}
                      alt="Background Preview"
                      className="w-full h-32 object-cover rounded-lg"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500">Use Media Manager to upload images and get URLs</p>
              </div>
            </div>

            {/* Height */}
            <div>
              <label className="block text-sm font-medium mb-1">Height</label>
              <select
                value={section.props.height || '400px'}
                onChange={(e) => handlePropChange('height', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="300px">Small (300px)</option>
                <option value="400px">Medium (400px)</option>
                <option value="500px">Large (500px)</option>
                <option value="600px">Extra Large (600px)</option>
              </select>
            </div>
          </div>
        );

      case 'pricing':
      case 'pricing_table':
      case 'announcement':
        return (
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Section Title</label>
              <input
                type="text"
                value={section.props.title || ''}
                onChange={(e) => handlePropChange('title', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Choose Your Plan"
              />
            </div>

            {/* Pricing Plans */}
            <div>
              <label className="block text-sm font-medium mb-2">Pricing Plans</label>
              <div className="space-y-2">
                {(section.props.plans || []).map((plan: any, index: number) => (
                  <div key={index} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{plan.name || `Plan ${index + 1}`}</span>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 text-sm">
                          <input
                            type="checkbox"
                            checked={plan.highlighted || false}
                            onChange={(e) => handleArrayItemChange('plans', index, 'highlighted', e.target.checked)}
                            className="rounded"
                          />
                          Featured
                        </label>
                        <button
                          onClick={() => removeArrayItem('plans', index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={plan.name || ''}
                        onChange={(e) => handleArrayItemChange('plans', index, 'name', e.target.value)}
                        placeholder="Plan name"
                        className="px-2 py-1 border rounded text-sm"
                      />
                      <div className="flex gap-1">
                        <input
                          type="text"
                          value={plan.price || ''}
                          onChange={(e) => handleArrayItemChange('plans', index, 'price', e.target.value)}
                          placeholder="$29"
                          className="flex-1 px-2 py-1 border rounded text-sm"
                        />
                        <input
                          type="text"
                          value={plan.period || ''}
                          onChange={(e) => handleArrayItemChange('plans', index, 'period', e.target.value)}
                          placeholder="month"
                          className="w-20 px-2 py-1 border rounded text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-gray-600 mb-1">Features (one per line)</p>
                      <textarea
                        value={(plan.features || []).join('\n')}
                        onChange={(e) => handleArrayItemChange('plans', index, 'features', e.target.value.split('\n').filter(f => f.trim()))}
                        placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                        className="w-full px-2 py-1 border rounded text-sm"
                        rows={3}
                      />
                    </div>

                    <input
                      type="text"
                      value={plan.buttonText || ''}
                      onChange={(e) => handleArrayItemChange('plans', index, 'buttonText', e.target.value)}
                      placeholder="Button text"
                      className="w-full px-2 py-1 border rounded text-sm"
                    />
                  </div>
                ))}

                <button
                  onClick={() => addArrayItem('plans', {
                    name: 'New Plan',
                    price: '$19',
                    period: 'month',
                    features: ['Feature 1', 'Feature 2', 'Feature 3'],
                    buttonText: 'Choose Plan',
                    highlighted: false
                  })}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Plan
                </button>
              </div>
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-4">
            {/* Text Blocks */}
            <div>
              <label className="block text-sm font-medium mb-2">Text Blocks</label>
              <div className="space-y-3">
                {(section.props.blocks || []).map((block: any, index: number) => (
                  <div key={index} className="border rounded-lg p-3 space-y-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Block {index + 1}</span>
                      <button
                        onClick={() => removeArrayItem('blocks', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Block Type */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-600">Type</label>
                        <select
                          value={block.type || 'p'}
                          onChange={(e) => handleArrayItemChange('blocks', index, 'type', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                        >
                          <option value="h1">Heading 1</option>
                          <option value="h2">Heading 2</option>
                          <option value="h3">Heading 3</option>
                          <option value="p">Paragraph</option>
                          <option value="quote">Quote</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-xs text-gray-600">Font</label>
                        <select
                          value={block.fontFamily || 'default'}
                          onChange={(e) => handleArrayItemChange('blocks', index, 'fontFamily', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                        >
                          <option value="default">Default</option>
                          <option value="serif">Serif</option>
                          <option value="sans-serif">Sans-serif</option>
                          <option value="monospace">Monospace</option>
                        </select>
                      </div>
                    </div>

                    {/* Content */}
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">Content</label>
                      <RichTextEditor
                        value={block.content || ''}
                        onChange={(value) => handleArrayItemChange('blocks', index, 'content', value)}
                        placeholder={
                          block.type === 'h1' ? 'Main Heading' :
                          block.type === 'h2' ? 'Subheading' :
                          block.type === 'h3' ? 'Section Title' :
                          block.type === 'quote' ? 'Quote text...' :
                          'Paragraph text...'
                        }
                        minHeight="100px"
                      />
                    </div>

                    {/* Styling Options */}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs text-gray-600">Size (px)</label>
                        <input
                          type="number"
                          value={block.fontSize || (block.type === 'h1' ? 32 : block.type === 'h2' ? 24 : block.type === 'h3' ? 20 : 16)}
                          onChange={(e) => handleArrayItemChange('blocks', index, 'fontSize', parseInt(e.target.value))}
                          className="w-full px-2 py-1 border rounded text-sm"
                          min="12"
                          max="72"
                        />
                      </div>

                      <div>
                        <label className="text-xs text-gray-600">Weight</label>
                        <select
                          value={block.fontWeight || (block.type.startsWith('h') ? 'bold' : 'normal')}
                          onChange={(e) => handleArrayItemChange('blocks', index, 'fontWeight', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                        >
                          <option value="300">Light</option>
                          <option value="normal">Regular</option>
                          <option value="500">Medium</option>
                          <option value="bold">Bold</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-xs text-gray-600">Align</label>
                        <select
                          value={block.textAlign || 'left'}
                          onChange={(e) => handleArrayItemChange('blocks', index, 'textAlign', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                          <option value="justify">Justify</option>
                        </select>
                      </div>
                    </div>

                    {/* Color */}
                    <div>
                      <label className="text-xs text-gray-600">Text Color</label>
                      <input
                        type="color"
                        value={block.color || '#000000'}
                        onChange={(e) => handleArrayItemChange('blocks', index, 'color', e.target.value)}
                        className="w-full h-8 border rounded cursor-pointer"
                      />
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => addArrayItem('blocks', {
                    type: 'p',
                    content: '',
                    fontSize: 16,
                    fontWeight: 'normal',
                    textAlign: 'left',
                    color: '#000000',
                    fontFamily: 'default'
                  })}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Text Block
                </button>
              </div>
            </div>

            {/* Legacy single content field - hidden but preserved for backward compatibility */}
            {section.props.content && !section.props.blocks?.length && (
              <div className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-800 mb-2">Legacy content detected. Click below to convert to new format:</p>
                <button
                  onClick={() => {
                    handlePropChange('blocks', [{
                      type: 'p',
                      content: section.props.content,
                      fontSize: section.props.fontSize === 'large' ? 20 : section.props.fontSize === 'small' ? 14 : 16,
                      textAlign: section.props.textAlign || 'left',
                      fontWeight: 'normal',
                      color: '#000000'
                    }]);
                    handlePropChange('content', '');
                  }}
                  className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                >
                  Convert to Blocks
                </button>
              </div>
            )}
          </div>
        );

      case 'image':
        return (
          <div className="space-y-4">
            {/* Image URL */}
            <div>
              <label className="block text-sm font-medium mb-1">Image URL</label>
              <div className="space-y-2">
                <input
                  type="text"
                  value={section.props.src || ''}
                  onChange={(e) => handlePropChange('src', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter image URL"
                />
                {section.props.src && (
                  <div className="relative">
                    <img
                      src={section.props.src}
                      alt={section.props.alt || 'Image'}
                      className="w-full h-32 object-cover rounded-lg"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500">Use Media Manager to upload images and get URLs</p>
              </div>
            </div>

            {/* Alt Text */}
            <div>
              <label className="block text-sm font-medium mb-1">Alt Text</label>
              <input
                type="text"
                value={section.props.alt || ''}
                onChange={(e) => handlePropChange('alt', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Image description"
              />
            </div>

            {/* Size Settings */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium mb-1">Width</label>
                <input
                  type="text"
                  value={section.props.width || '100%'}
                  onChange={(e) => handlePropChange('width', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="100% or 500px"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Height</label>
                <input
                  type="text"
                  value={section.props.height || 'auto'}
                  onChange={(e) => handlePropChange('height', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="auto or 400px"
                />
              </div>
            </div>
          </div>
        );

      case 'button':
        return (
          <div className="space-y-4">
            {/* Button Text */}
            <div>
              <label className="block text-sm font-medium mb-1">Button Text</label>
              <input
                type="text"
                value={section.props.text || ''}
                onChange={(e) => handlePropChange('text', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Click Me"
              />
            </div>

            {/* Button Link */}
            <div>
              <label className="block text-sm font-medium mb-1">Link</label>
              <input
                type="text"
                value={section.props.link || ''}
                onChange={(e) => handlePropChange('link', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="/page or https://..."
              />
            </div>

            {/* Button Style */}
            <div>
              <label className="block text-sm font-medium mb-1">Style</label>
              <select
                value={section.props.style || 'primary'}
                onChange={(e) => handlePropChange('style', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="primary">Primary (Blue)</option>
                <option value="secondary">Secondary (Gray)</option>
                <option value="outline">Outline</option>
              </select>
            </div>

            {/* Button Size */}
            <div>
              <label className="block text-sm font-medium mb-1">Size</label>
              <select
                value={section.props.size || 'medium'}
                onChange={(e) => handlePropChange('size', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="space-y-4">
            {/* Video URL */}
            <div>
              <label className="block text-sm font-medium mb-1">Video URL</label>
              <input
                type="text"
                value={section.props.url || ''}
                onChange={(e) => handlePropChange('url', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="https://example.com/video.mp4"
              />
              <p className="text-xs text-gray-500 mt-1">Supports MP4, WebM, and Ogg formats</p>
            </div>

            {/* Video Settings */}
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={section.props.autoplay || false}
                  onChange={(e) => handlePropChange('autoplay', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Autoplay</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={section.props.controls !== false}
                  onChange={(e) => handlePropChange('controls', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Show Controls</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={section.props.loop || false}
                  onChange={(e) => handlePropChange('loop', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Loop</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={section.props.muted || false}
                  onChange={(e) => handlePropChange('muted', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Muted</span>
              </label>
            </div>
          </div>
        );

      case 'spacer':
        return (
          <div className="space-y-4">
            {/* Spacer Height */}
            <div>
              <label className="block text-sm font-medium mb-1">Height</label>
              <input
                type="text"
                value={section.props.height || '50px'}
                onChange={(e) => handlePropChange('height', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="50px or 5rem"
              />
              <p className="text-xs text-gray-500 mt-1">Use px, rem, em, or vh units</p>
            </div>

            {/* Preview */}
            <div>
              <label className="block text-sm font-medium mb-1">Preview</label>
              <div
                className="border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400"
                style={{ height: section.props.height || '50px' }}
              >
                Spacer: {section.props.height || '50px'}
              </div>
            </div>
          </div>
        );

      case 'premium_cards':
        return (
          <div className="space-y-4">
            {/* Section Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Section Title</label>
              <input
                type="text"
                value={section.props.title || ''}
                onChange={(e) => handlePropChange('title', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Explore Our Features"
              />
            </div>

            {/* Section Subtitle */}
            <div>
              <label className="block text-sm font-medium mb-1">Section Subtitle</label>
              <input
                type="text"
                value={section.props.subtitle || ''}
                onChange={(e) => handlePropChange('subtitle', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Discover what makes us special"
              />
            </div>

            {/* Card Variant */}
            <div>
              <label className="block text-sm font-medium mb-1">Card Style</label>
              <select
                value={section.props.variant || 'appstore'}
                onChange={(e) => handlePropChange('variant', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="appstore">App Store</option>
                <option value="netflix">Netflix</option>
                <option value="glass">Glass</option>
                <option value="overlay">Overlay</option>
              </select>
            </div>

            {/* Scrolling Options */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="scrollable"
                checked={section.props.scrollable !== false}
                onChange={(e) => handlePropChange('scrollable', e.target.checked)}
                className="rounded"
              />
              <label htmlFor="scrollable" className="text-sm">Enable Horizontal Scrolling</label>
            </div>

            {/* Card Width */}
            <div>
              <label className="block text-sm font-medium mb-1">Card Width</label>
              <select
                value={section.props.cardWidth || 'medium'}
                onChange={(e) => handlePropChange('cardWidth', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="small">Small (12rem)</option>
                <option value="medium">Medium (16rem)</option>
                <option value="large">Large (20rem)</option>
                <option value="xl">Extra Large (24rem)</option>
              </select>
            </div>

            {/* Card Items */}
            <div>
              <label className="block text-sm font-medium mb-2">Cards</label>
              <div className="space-y-3">
                {(section.props.items || []).map((item: any, index: number) => (
                  <div key={index} className="border rounded-lg p-4 space-y-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold">Card {index + 1}</span>
                      <button
                        onClick={() => removeArrayItem('items', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Title */}
                    <input
                      type="text"
                      value={item.title || ''}
                      onChange={(e) => handleArrayItemChange('items', index, 'title', e.target.value)}
                      placeholder="Card Title"
                      className="w-full px-2 py-1 border rounded text-sm"
                    />

                    {/* Subtitle */}
                    <input
                      type="text"
                      value={item.subtitle || ''}
                      onChange={(e) => handleArrayItemChange('items', index, 'subtitle', e.target.value)}
                      placeholder="Card Subtitle"
                      className="w-full px-2 py-1 border rounded text-sm"
                    />

                    {/* Description */}
                    <textarea
                      value={item.description || ''}
                      onChange={(e) => handleArrayItemChange('items', index, 'description', e.target.value)}
                      placeholder="Card Description"
                      className="w-full px-2 py-1 border rounded text-sm"
                      rows={2}
                    />

                    {/* Image Section */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-700">Image</label>
                      {item.image && (
                        <div className="relative">
                          <img
                            src={item.image}
                            alt="Card image"
                            className="w-full h-24 object-cover rounded"
                          />
                          <button
                            onClick={() => handleArrayItemChange('items', index, 'image', '')}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <input
                        type="text"
                        value={item.image || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'image', e.target.value)}
                        placeholder="Image URL (use Media Manager for uploads)"
                        className="w-full px-2 py-1 border rounded text-sm"
                      />

                      {/* Image Settings */}
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={item.imageAspectRatio || 'auto'}
                          onChange={(e) => handleArrayItemChange('items', index, 'imageAspectRatio', e.target.value)}
                          className="px-2 py-1 border rounded text-xs"
                        >
                          <option value="auto">Auto Aspect</option>
                          <option value="16:9">16:9</option>
                          <option value="4:3">4:3</option>
                          <option value="1:1">Square</option>
                          <option value="3:2">3:2</option>
                        </select>

                        <select
                          value={item.imageFit || 'cover'}
                          onChange={(e) => handleArrayItemChange('items', index, 'imageFit', e.target.value)}
                          className="px-2 py-1 border rounded text-xs"
                        >
                          <option value="cover">Cover</option>
                          <option value="contain">Contain</option>
                          <option value="fill">Fill</option>
                          <option value="scale-down">Scale Down</option>
                        </select>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-gray-700">Call to Action</label>
                      <input
                        type="text"
                        value={item.ctaText || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'ctaText', e.target.value)}
                        placeholder="Button Text"
                        className="w-full px-2 py-1 border rounded text-sm"
                      />
                      <div className="flex gap-2">
                        <select
                          value={item.linkType || 'internal'}
                          onChange={(e) => handleArrayItemChange('items', index, 'linkType', e.target.value)}
                          className="px-2 py-1 border rounded text-sm"
                        >
                          <option value="internal">Internal</option>
                          <option value="external">External</option>
                        </select>
                        <input
                          type="text"
                          value={item.link || ''}
                          onChange={(e) => handleArrayItemChange('items', index, 'link', e.target.value)}
                          placeholder={item.linkType === 'external' ? 'https://...' : '/page-route'}
                          className="flex-1 px-2 py-1 border rounded text-sm"
                        />
                      </div>
                    </div>

                    {/* Badge */}
                    <input
                      type="text"
                      value={item.badge || ''}
                      onChange={(e) => handleArrayItemChange('items', index, 'badge', e.target.value)}
                      placeholder="Badge Text (e.g., NEW, POPULAR)"
                      className="w-full px-2 py-1 border rounded text-sm"
                    />

                    {/* Overlay-specific fields */}
                    {section.props.variant === 'overlay' && (
                      <>
                        <div>
                          <label className="text-xs font-medium text-gray-700">Text Position</label>
                          <select
                            value={item.textPosition || 'bottom'}
                            onChange={(e) => handleArrayItemChange('items', index, 'textPosition', e.target.value)}
                            className="w-full px-2 py-1 border rounded text-sm"
                          >
                            <option value="top">Top</option>
                            <option value="center">Center</option>
                            <option value="bottom">Bottom</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-xs font-medium text-gray-700">
                            Overlay Intensity: {Math.round((item.overlayIntensity || 0.7) * 100)}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={(item.overlayIntensity || 0.7) * 100}
                            onChange={(e) => handleArrayItemChange('items', index, 'overlayIntensity', parseInt(e.target.value) / 100)}
                            className="w-full"
                          />
                        </div>
                      </>
                    )}

                    {/* App Store specific - Rating */}
                    {section.props.variant === 'appstore' && (
                      <div>
                        <label className="text-xs font-medium text-gray-700">Rating (1-5)</label>
                        <input
                          type="number"
                          min="1"
                          max="5"
                          value={item.rating || ''}
                          onChange={(e) => handleArrayItemChange('items', index, 'rating', parseInt(e.target.value))}
                          placeholder="Rating"
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      </div>
                    )}

                    {/* Accent Color */}
                    <div>
                      <label className="text-xs font-medium text-gray-700">Accent Color</label>
                      <input
                        type="text"
                        value={item.accentColor || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'accentColor', e.target.value)}
                        placeholder="#667eea or rgb(102, 126, 234)"
                        className="w-full px-2 py-1 border rounded text-sm"
                      />
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => addArrayItem('items', {
                    id: `card_${Date.now()}`,
                    title: 'New Card',
                    subtitle: 'Card subtitle',
                    description: 'Card description',
                    image: '',
                    link: '',
                    linkType: 'internal',
                    ctaText: 'Learn More',
                    badge: '',
                    rating: null,
                    accentColor: '#667eea',
                    imageAspectRatio: 'auto',
                    imageFit: 'cover',
                    textPosition: 'bottom',
                    overlayIntensity: 0.7
                  })}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Card
                </button>
              </div>
            </div>
          </div>
        );

      case 'content':
        return (
          <div className="space-y-4">
            {/* Content Variant */}
            <div>
              <label className="block text-sm font-medium mb-1">Content Style</label>
              <select
                value={section.props.variant || 'magazine'}
                onChange={(e) => handlePropChange('variant', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="magazine">Magazine Article</option>
                <option value="blog">Blog Post</option>
                <option value="feature">Feature Story</option>
                <option value="faq">FAQ Section</option>
                <option value="quote">Testimonials</option>
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                type="text"
                value={section.props.title || ''}
                onChange={(e) => handlePropChange('title', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Section title"
              />
            </div>

            {/* Subtitle */}
            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <input
                type="text"
                value={section.props.subtitle || ''}
                onChange={(e) => handlePropChange('subtitle', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Section subtitle"
              />
            </div>

            {/* Typography Controls */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600">Title Size</label>
                <select
                  value={section.props.titleSize || 'text-3xl'}
                  onChange={(e) => handlePropChange('titleSize', e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="text-xl">XL</option>
                  <option value="text-2xl">2XL</option>
                  <option value="text-3xl">3XL</option>
                  <option value="text-4xl">4XL</option>
                  <option value="text-5xl">5XL</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600">Title Align</label>
                <select
                  value={section.props.titleAlign || 'left'}
                  onChange={(e) => handlePropChange('titleAlign', e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                  <option value="justify">Justify</option>
                </select>
              </div>
            </div>

            {/* Content Typography */}
            <div className="grid grid-cols-1 gap-2">
              <div>
                <label className="text-xs text-gray-600">Content Size</label>
                <select
                  value={section.props.contentSize || 'text-base'}
                  onChange={(e) => handlePropChange('contentSize', e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="text-sm">Small</option>
                  <option value="text-base">Base</option>
                  <option value="text-lg">Large</option>
                  <option value="text-xl">Extra Large</option>
                </select>
              </div>
            </div>

            {/* Alignment Helper */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>💡 Text Alignment:</strong> Use the alignment buttons in the content editor toolbar below to align text left, center, right, or justify.
              </p>
            </div>

            {/* Additional Typography Controls */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600">Line Height</label>
                <select
                  value={section.props.lineHeight || 'normal'}
                  onChange={(e) => handlePropChange('lineHeight', e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="tight">Tight</option>
                  <option value="snug">Snug</option>
                  <option value="normal">Normal</option>
                  <option value="relaxed">Relaxed</option>
                  <option value="loose">Loose</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600">Font Weight</label>
                <select
                  value={section.props.contentWeight || 'normal'}
                  onChange={(e) => handlePropChange('contentWeight', e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="light">Light</option>
                  <option value="normal">Normal</option>
                  <option value="medium">Medium</option>
                  <option value="semibold">Semibold</option>
                  <option value="bold">Bold</option>
                </select>
              </div>
            </div>

            {/* Columns (for magazine and quote variants) */}
            {(section.props.variant === 'magazine' || section.props.variant === 'quote') && (
              <div>
                <label className="text-xs text-gray-600">Columns</label>
                <select
                  value={section.props.columns || 1}
                  onChange={(e) => handlePropChange('columns', parseInt(e.target.value))}
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="1">1 Column</option>
                  <option value="2">2 Columns</option>
                  <option value="3">3 Columns</option>
                </select>
              </div>
            )}

            {/* Image Position (for feature variant) */}
            {section.props.variant === 'feature' && (
              <div>
                <label className="text-xs text-gray-600">Image Position</label>
                <select
                  value={section.props.imagePosition || 'left'}
                  onChange={(e) => handlePropChange('imagePosition', e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                  <option value="top">Top</option>
                </select>
              </div>
            )}

            {/* Colors */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600">Text Color</label>
                <input
                  type="color"
                  value={section.props.textColor || '#1f2937'}
                  onChange={(e) => handlePropChange('textColor', e.target.value)}
                  className="w-full h-8 border rounded"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Accent Color</label>
                <input
                  type="color"
                  value={section.props.accentColor || '#3b82f6'}
                  onChange={(e) => handlePropChange('accentColor', e.target.value)}
                  className="w-full h-8 border rounded"
                />
              </div>
            </div>

            {/* Content Items */}
            <div>
              <label className="block text-sm font-medium mb-2">Content Items</label>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {(section.props.items || []).map((item: any, index: number) => (
                  <div key={item.id || index} className="border rounded-lg p-3 space-y-2">
                    {/* Item Title */}
                    <input
                      type="text"
                      value={item.title || ''}
                      onChange={(e) => handleArrayItemChange('items', index, 'title', e.target.value)}
                      className="w-full px-2 py-1 border rounded text-sm"
                      placeholder="Item title"
                    />

                    {/* Item Subtitle (for feature variant) */}
                    {section.props.variant === 'feature' && (
                      <input
                        type="text"
                        value={item.subtitle || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'subtitle', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                        placeholder="Subtitle"
                      />
                    )}

                    {/* Content */}
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">Content</label>
                      <RichTextEditor
                        value={item.content || ''}
                        onChange={(value) => handleArrayItemChange('items', index, 'content', value)}
                        placeholder={section.props.variant === 'faq' ?
                          'Enter your answer here...' :
                          'Enter your content here...'}
                        minHeight="150px"
                      />
                    </div>

                    {/* Author (for magazine, blog, quote) */}
                    {(section.props.variant === 'magazine' || section.props.variant === 'blog' || section.props.variant === 'quote') && (
                      <input
                        type="text"
                        value={item.author || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'author', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                        placeholder="Author name"
                      />
                    )}

                    {/* Date (for blog) */}
                    {section.props.variant === 'blog' && (
                      <input
                        type="text"
                        value={item.date || ''}
                        onChange={(e) => handleArrayItemChange('items', index, 'date', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                        placeholder="Date (e.g., March 15, 2024)"
                      />
                    )}

                    {/* Image (for blog and feature) */}
                    {(section.props.variant === 'blog' || section.props.variant === 'feature') && (
                      <div>
                        <label className="text-xs text-gray-600">Image</label>
                        <input
                          type="text"
                          value={item.image || ''}
                          onChange={(e) => handleArrayItemChange('items', index, 'image', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="Image URL (use Media Manager for uploads)"
                        />
                        {item.image && (
                          <img src={item.image} alt="Preview" className="mt-2 h-20 object-cover rounded" />
                        )}
                      </div>
                    )}

                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={() => removeArrayItem('items', index)}
                      className="text-red-600 text-sm hover:text-red-700"
                    >
                      Remove Item
                    </button>
                  </div>
                ))}

                {/* Add Item Button */}
                <button
                  type="button"
                  onClick={() => {
                    const newItem: any = {
                      id: `item_${Date.now()}`,
                      title: section.props.variant === 'faq' ? 'New Question' : 'New Item',
                      content: section.props.variant === 'faq' ? '<p>Answer text here...</p>' : '<p>Content here...</p>'
                    };

                    // Add variant-specific fields
                    if (section.props.variant === 'blog') {
                      newItem.date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
                      newItem.author = 'Author Name';
                    }
                    if (section.props.variant === 'feature') {
                      newItem.subtitle = 'Subtitle here';
                      newItem.image = '';
                    }
                    if (section.props.variant === 'quote') {
                      newItem.author = 'Person Name';
                      newItem.title = 'Title/Position';
                    }

                    addArrayItem('items', newItem);
                  }}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add {section.props.variant === 'faq' ? 'Question' : 'Item'}
                </button>
              </div>
            </div>
          </div>
        );

      case 'button_grid':
        return (
          <div className="space-y-6">
            {/* Typography Section */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-3 text-sm text-gray-700">Typography</h4>
              <div className="space-y-3">
                {/* Title */}
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-600">Title</label>
                  <input
                    type="text"
                    value={section.props.title || ''}
                    onChange={(e) => handlePropChange('title', e.target.value)}
                    className="w-full px-3 py-1.5 border rounded text-sm"
                    placeholder="Section title"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Title Size</label>
                    <select
                      value={section.props.titleSize || 'text-2xl'}
                      onChange={(e) => handlePropChange('titleSize', e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      <option value="text-lg">Small</option>
                      <option value="text-xl">Medium</option>
                      <option value="text-2xl">Large</option>
                      <option value="text-3xl">X-Large</option>
                      <option value="text-4xl">2X-Large</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Title Weight</label>
                    <select
                      value={section.props.titleWeight || 'bold'}
                      onChange={(e) => handlePropChange('titleWeight', e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      <option value="normal">Normal</option>
                      <option value="medium">Medium</option>
                      <option value="semibold">Semibold</option>
                      <option value="bold">Bold</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-600">Title Alignment</label>
                  <select
                    value={section.props.titleAlign || 'center'}
                    onChange={(e) => handlePropChange('titleAlign', e.target.value)}
                    className="w-full px-2 py-1.5 border rounded text-sm"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>

                {/* Subtitle */}
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-600">Subtitle</label>
                  <input
                    type="text"
                    value={section.props.subtitle || ''}
                    onChange={(e) => handlePropChange('subtitle', e.target.value)}
                    className="w-full px-3 py-1.5 border rounded text-sm"
                    placeholder="Section subtitle (optional)"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-600">Subtitle Size</label>
                  <select
                    value={section.props.subtitleSize || 'text-base'}
                    onChange={(e) => handlePropChange('subtitleSize', e.target.value)}
                    className="w-full px-2 py-1.5 border rounded text-sm"
                  >
                    <option value="text-sm">Small</option>
                    <option value="text-base">Medium</option>
                    <option value="text-lg">Large</option>
                    <option value="text-xl">X-Large</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Spacing Section */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-3 text-sm text-gray-700">Spacing</h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Container Padding</label>
                    <select
                      value={section.props.containerPadding || '24px'}
                      onChange={(e) => handlePropChange('containerPadding', e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      <option value="8px">Compact (8px)</option>
                      <option value="16px">Small (16px)</option>
                      <option value="24px">Medium (24px)</option>
                      <option value="32px">Large (32px)</option>
                      <option value="48px">X-Large (48px)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Button Gap</label>
                    <select
                      value={section.props.gap || 16}
                      onChange={(e) => handlePropChange('gap', parseInt(e.target.value))}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      <option value={8}>Small (8px)</option>
                      <option value={12}>Medium (12px)</option>
                      <option value={16}>Large (16px)</option>
                      <option value={20}>X-Large (20px)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Title Margin</label>
                    <select
                      value={section.props.titleMarginBottom || 8}
                      onChange={(e) => handlePropChange('titleMarginBottom', parseInt(e.target.value))}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      <option value={4}>Small (4px)</option>
                      <option value={8}>Medium (8px)</option>
                      <option value={12}>Large (12px)</option>
                      <option value={16}>X-Large (16px)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Subtitle Margin</label>
                    <select
                      value={section.props.subtitleMarginBottom || 24}
                      onChange={(e) => handlePropChange('subtitleMarginBottom', parseInt(e.target.value))}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      <option value={12}>Small (12px)</option>
                      <option value={16}>Medium (16px)</option>
                      <option value={24}>Large (24px)</option>
                      <option value={32}>X-Large (32px)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Button Style Section */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-3 text-sm text-gray-700">Button Style</h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Variant</label>
                    <select
                      value={section.props.variant || 'default'}
                      onChange={(e) => handlePropChange('variant', e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      <option value="default">Default</option>
                      <option value="rounded">Rounded</option>
                      <option value="card">Card</option>
                      <option value="gradient">Gradient</option>
                      <option value="horizontal">Horizontal</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Grid Size</label>
                    <select
                      value={section.props.gridSize || 4}
                      onChange={(e) => handlePropChange('gridSize', parseInt(e.target.value))}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      <option value={2}>2 Buttons</option>
                      <option value={4}>4 Buttons</option>
                      <option value={6}>6 Buttons</option>
                      <option value={8}>8 Buttons</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Button Height</label>
                    <select
                      value={section.props.buttonHeight || '120px'}
                      onChange={(e) => handlePropChange('buttonHeight', e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      <option value="80px">Small</option>
                      <option value="100px">Medium</option>
                      <option value="120px">Large</option>
                      <option value="140px">X-Large</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Button Padding</label>
                    <select
                      value={section.props.buttonPadding || '16px'}
                      onChange={(e) => handlePropChange('buttonPadding', e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      <option value="8px">Small</option>
                      <option value="12px">Medium</option>
                      <option value="16px">Large</option>
                      <option value="20px">X-Large</option>
                    </select>
                  </div>
                </div>

                {section.props.variant === 'horizontal' && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium mb-1 text-gray-600">Icon Size</label>
                        <select
                          value={section.props.horizontalIconSize || 24}
                          onChange={(e) => handlePropChange('horizontalIconSize', parseInt(e.target.value))}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                        >
                          <option value={18}>Small</option>
                          <option value={24}>Medium</option>
                          <option value={28}>Large</option>
                          <option value={32}>X-Large</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-gray-600">Text Size</label>
                        <select
                          value={section.props.horizontalTextSize || 'text-sm'}
                          onChange={(e) => handlePropChange('horizontalTextSize', e.target.value)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                        >
                          <option value="text-xs">X-Small</option>
                          <option value="text-sm">Small</option>
                          <option value="text-base">Medium</option>
                          <option value="text-lg">Large</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}

                {section.props.variant !== 'horizontal' && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium mb-1 text-gray-600">Button Text Size</label>
                        <select
                          value={section.props.buttonTextSize || 'text-sm'}
                          onChange={(e) => handlePropChange('buttonTextSize', e.target.value)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                        >
                          <option value="text-xs">X-Small</option>
                          <option value="text-sm">Small</option>
                          <option value="text-base">Medium</option>
                          <option value="text-lg">Large</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-gray-600">Icon Size</label>
                        <select
                          value={section.props.iconSize || 'medium'}
                          onChange={(e) => handlePropChange('iconSize', e.target.value)}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                        >
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div>
              <label className="block text-sm font-medium mb-2">Buttons</label>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {(section.props.buttons || []).map((button: any, index: number) => (
                  <div key={button.id || index} className="border rounded-lg p-3 space-y-2">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">Button {index + 1}</span>
                      <button
                        onClick={() => {
                          const newButtons = [...(section.props.buttons || [])];
                          newButtons.splice(index, 1);
                          handlePropChange('buttons', newButtons);
                        }}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>

                    {/* Icon */}
                    <div>
                      <label className="text-xs text-gray-600">Icon (Emoji or URL)</label>
                      <input
                        type="text"
                        value={button.icon || ''}
                        onChange={(e) => handleArrayItemChange('buttons', index, 'icon', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                        placeholder="📚 or https://..."
                      />
                    </div>

                    {/* Title */}
                    <div>
                      <label className="text-xs text-gray-600">Title</label>
                      <input
                        type="text"
                        value={button.title || ''}
                        onChange={(e) => handleArrayItemChange('buttons', index, 'title', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                        placeholder="Button title"
                      />
                    </div>

                    {/* Subtitle (optional) */}
                    <div>
                      <label className="text-xs text-gray-600">Subtitle (optional)</label>
                      <input
                        type="text"
                        value={button.subtitle || ''}
                        onChange={(e) => handleArrayItemChange('buttons', index, 'subtitle', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                        placeholder="Button subtitle"
                      />
                    </div>

                    {/* Link Type */}
                    <div>
                      <label className="text-xs text-gray-600">Link Type</label>
                      <select
                        value={button.linkType || 'internal'}
                        onChange={(e) => handleArrayItemChange('buttons', index, 'linkType', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="internal">Internal Page</option>
                        <option value="external">External URL</option>
                      </select>
                    </div>

                    {/* Link */}
                    <div>
                      <label className="text-xs text-gray-600">Link</label>
                      <input
                        type="text"
                        value={button.link || ''}
                        onChange={(e) => handleArrayItemChange('buttons', index, 'link', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                        placeholder={button.linkType === 'external' ? 'https://...' : '/page-route'}
                      />
                    </div>

                    {/* Background Color (for gradient variant) */}
                    {section.props.variant === 'gradient' && (
                      <div>
                        <label className="text-xs text-gray-600">Background</label>
                        <input
                          type="text"
                          value={button.backgroundColor || ''}
                          onChange={(e) => handleArrayItemChange('buttons', index, 'backgroundColor', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          placeholder="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Button */}
              <button
                onClick={() => {
                  const newButton = {
                    id: `btn-${Date.now()}`,
                    icon: '📚',
                    title: 'New Button',
                    link: '/',
                    linkType: 'internal'
                  };
                  const currentButtons = section.props.buttons || [];
                  if (currentButtons.length < (section.props.gridSize || 4)) {
                    handlePropChange('buttons', [...currentButtons, newButton]);
                  }
                }}
                disabled={(section.props.buttons || []).length >= (section.props.gridSize || 4)}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Button
              </button>
            </div>
          </div>
        );

      case 'text_card':
        return (
          <div className="space-y-4">
            {/* Heading */}
            <div>
              <label className="block text-sm font-medium mb-1">Heading</label>
              <input
                type="text"
                value={section.props.heading || ''}
                onChange={(e) => handlePropChange('heading', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter heading text"
              />
            </div>

            {/* Heading Controls */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Heading Font</label>
                <select
                  value={section.props.headingFont || 'default'}
                  onChange={(e) => handlePropChange('headingFont', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="default">System Default</option>
                  <option value="serif">Serif</option>
                  <option value="sans">Sans-serif</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Heading Alignment</label>
                <select
                  value={section.props.headingAlign || 'left'}
                  onChange={(e) => handlePropChange('headingAlign', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>

            {/* Body Text */}
            <div>
              <label className="block text-sm font-medium mb-1">Body Text</label>
              <RichTextEditor
                value={section.props.bodyText || ''}
                onChange={(value) => handlePropChange('bodyText', value)}
                placeholder="Enter body text with formatting..."
                minHeight="200px"
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Use editor toolbar buttons for text formatting and alignment
              </p>
            </div>

            {/* Body Font */}
            <div>
              <label className="block text-sm font-medium mb-1">Body Font</label>
              <select
                value={section.props.bodyFont || 'default'}
                onChange={(e) => handlePropChange('bodyFont', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="default">System Default</option>
                <option value="serif">Serif</option>
                <option value="sans">Sans-serif</option>
              </select>
            </div>

            {/* Card Styling */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3 text-sm">Card Styling</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Border Radius (px)</label>
                  <input
                    type="number"
                    value={section.props.borderRadius || 12}
                    onChange={(e) => handlePropChange('borderRadius', parseInt(e.target.value) || 12)}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                    max="50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Shadow Level</label>
                  <select
                    value={section.props.shadowLevel || 'md'}
                    onChange={(e) => handlePropChange('shadowLevel', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="none">None</option>
                    <option value="sm">Small</option>
                    <option value="md">Medium</option>
                    <option value="lg">Large</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center text-gray-500 py-8">
            <p>Content editor for "{sectionType}" is not yet available.</p>
            <p className="text-sm mt-2">You can edit the raw props in Advanced tab.</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">

      {renderContentFields()}
    </div>
  );
};